import React from 'react';
import {
	Box,
	Flex,
	HStack,
	VStack,
	Text,
	Icon,
	Button,
	IconButton,
	Input,
	Textarea,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	FormControl,
	FormLabel,
	FormHelperText,
} from '@chakra-ui/react';
import { FiZap, FiSettings, FiTrash2, FiPlus } from 'react-icons/fi';
import { usePrefs, prefs } from '../hooks/useLocalPrefs';

/**
 * Quick replies — WhatsApp Business's "/" shortcuts.
 *
 * Typing "/" at the start of the box lists saved replies filtered by what
 * follows; arrows move, Enter inserts. Saved per browser (useLocalPrefs) until
 * the server has a place for them, and the manager says so.
 */
export function matchQuickReplies(list, token) {
	const q = String(token ?? '').trim().toLowerCase();
	if (!q) return list;
	return list.filter(
		(r) => r.shortcut.toLowerCase().includes(q) || r.text.toLowerCase().includes(q),
	);
}

export function QuickReplyList({ matches, activeIndex, onPick, onManage }) {
	return (
		<Box
			position="absolute"
			left="3"
			right="3"
			bottom="100%"
			mb="1"
			bg="wa.menu"
			borderWidth="1px"
			borderColor="wa.divider"
			borderRadius="lg"
			boxShadow="lg"
			overflow="hidden"
			zIndex="3"
			role="listbox"
			aria-label="Quick replies"
		>
			<Flex px="3" py="1.5" align="center" justify="space-between" borderBottomWidth="1px" borderColor="wa.divider">
				<HStack spacing="1.5">
					<Icon as={FiZap} boxSize="14px" color="wa.accent" />
					<Text fontSize="xs" color="wa.inkMuted">
						Quick replies · on this device
					</Text>
				</HStack>
				<Button size="xs" variant="ghost" leftIcon={<Icon as={FiSettings} />} onClick={onManage} color="wa.inkMuted">
					Manage
				</Button>
			</Flex>
			<Box maxH="220px" overflowY="auto">
				{matches.length === 0 && (
					<Text px="3" py="3" fontSize="sm" color="wa.inkMuted">
						No quick reply matches. Keep typing, or add one under Manage.
					</Text>
				)}
				{matches.map((r, i) => (
					<Box
						key={r.id}
						role="option"
						aria-selected={i === activeIndex}
						px="3"
						py="2"
						cursor="pointer"
						bg={i === activeIndex ? 'wa.hover' : 'transparent'}
						_hover={{ bg: 'wa.hover' }}
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => onPick(r)}
					>
						<Text fontSize="xs" fontWeight="semibold" color="wa.accent">
							/{r.shortcut}
						</Text>
						<Text fontSize="sm" color="wa.ink" noOfLines={2}>
							{r.text}
						</Text>
					</Box>
				))}
			</Box>
		</Box>
	);
}

const newId = () => `qr_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function QuickReplyManager({ isOpen, onClose }) {
	const p = usePrefs();
	const [shortcut, setShortcut] = React.useState('');
	const [text, setText] = React.useState('');

	const add = () => {
		const s = shortcut.trim().replace(/^\//, '').replace(/\s+/g, '-').toLowerCase();
		if (!s || !text.trim()) return;
		prefs.saveQuickReply({ id: newId(), shortcut: s, text: text.trim() });
		setShortcut('');
		setText('');
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
			<ModalOverlay />
			<ModalContent bg="wa.menu" color="wa.ink">
				<ModalHeader fontSize="md">Quick replies</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Text fontSize="xs" color="wa.inkMuted" mb="3">
						Type <b>/</b> in the message box to use one. Saved on this device only.
					</Text>
					<VStack align="stretch" spacing="2" maxH="220px" overflowY="auto" mb="4">
						{p.quickReplies.length === 0 && (
							<Text fontSize="sm" color="wa.inkMuted">
								None yet.
							</Text>
						)}
						{p.quickReplies.map((r) => (
							<Flex key={r.id} align="flex-start" borderWidth="1px" borderColor="wa.divider" borderRadius="md" px="3" py="2">
								<Box flex="1" minW="0">
									<Text fontSize="xs" fontWeight="semibold" color="wa.accent">
										/{r.shortcut}
									</Text>
									<Text fontSize="sm" whiteSpace="pre-wrap">
										{r.text}
									</Text>
								</Box>
								<IconButton
									aria-label="Delete quick reply"
									icon={<Icon as={FiTrash2} />}
									size="xs"
									variant="ghost"
									color="wa.inkMuted"
									onClick={() => prefs.removeQuickReply(r.id)}
								/>
							</Flex>
						))}
					</VStack>
					<FormControl mb="2">
						<FormLabel fontSize="xs" color="wa.inkMuted">
							Shortcut
						</FormLabel>
						<Input size="sm" value={shortcut} onChange={(e) => setShortcut(e.target.value)} placeholder="e.g. pricing" bg="wa.input" />
					</FormControl>
					<FormControl>
						<FormLabel fontSize="xs" color="wa.inkMuted">
							Message
						</FormLabel>
						<Textarea size="sm" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="The full reply" bg="wa.input" />
						<FormHelperText fontSize="xs">Shift+Enter for a new line when using it.</FormHelperText>
					</FormControl>
				</ModalBody>
				<ModalFooter>
					<Button size="sm" variant="ghost" mr="2" onClick={onClose}>
						Close
					</Button>
					<Button
						size="sm"
						bg="wa.accent"
						color="wa.accentInk"
						_hover={{ bg: 'wa.accentHover' }}
						leftIcon={<Icon as={FiPlus} />}
						onClick={add}
						isDisabled={!shortcut.trim() || !text.trim()}
					>
						Add
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
